/**
 * 業務ルール 1 件の区画 — 入力・出力、中身 4 種（判定表 / 式 / 並べ替え / 変更）、読まれる先、
 * 使っている業務。業務詳細（データの流れの図）のモーダルと業務ルールの画面が同じものを使う。
 *
 * **判定表はデシジョンテーブルで出す。** 条件の列 → 出力の列、行 ＝ 規則、見ない条件は「—」。
 * ヒットの決まりと「—」の凡例は「#」の見出しの ⓘ に畳む。規則文の ID は出さない（業務の外では意味を持たない）。
 * 判定表・式の行の文は `rule-body-parts.tsx`、並べ替えとキーの決め方は `rule-sort-body.tsx` が持つ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルール 1 件の入力・出力・中身 4 種・読まれる先・使っている業務を描き、案件の突き合わせで足した・外した項目に地色を敷く。
 * @graph-connects RuleLink [calls] 業務ルールの画面へのリンク
 * @graph-connects BusinessDetailLink [calls] 業務詳細へのリンク
 */
import type { ElementRef, RuleBodyView, RuleView } from '@cortex/domain-spec-console';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { specDiffCellClass, type SpecDiffState } from '../components/spec/SpecDiffState.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import {
  DecisionTableBody,
  ElementRefText,
  FormulaLines,
  isMissingOn,
  MUTED_CLASS,
  RuleFieldRow,
  type RuleCompareProps,
} from './rule-body-parts.js';
import { SortBody } from './rule-sort-body.js';
import { BusinessDetailLink, RuleLink } from './view-links/business-logic.js';

/**
 * 業務ルールの中身の同一性。読まれる先・使っている業務・入力の出し手は、ほかの業務ルールの
 * 増減だけで動くので入れない（入れると触っていない業務ルールに「変更」が付く）。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの中身（名前・形・CRUD・入力の用語・出力・中身・実装）を 1 本の文字列にする
 * @graph-connects none
 */
export function ruleSignature(rule: RuleView): string {
  return JSON.stringify([
    rule.label,
    rule.kind,
    rule.crud,
    rule.inputs.map((input) => input.term),
    rule.output,
    rule.body,
    rule.implementation,
  ]);
}

/**
 * 変更前と変更後の業務ルールから、案件がそれをどうしたかを決める。
 *
 * @param before 変更前の姿。無ければ null
 * @param after 変更後の姿。無ければ null
 * @returns 追加 / 削除 / 変更 / 変わっていない
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの変更前と変更後を比べ、追加・削除・変更・変わっていないのどれかを返す。
 * @graph-connects none
 */
export function ruleChangeState(before: RuleView | null, after: RuleView | null): SpecDiffState {
  if (before === null) return 'created';
  if (after === null) return 'deleted';
  return ruleSignature(before) === ruleSignature(after) ? 'unchanged' : 'updated';
}

/** `FormulaBody` の props。 @graph-connects none */
interface FormulaBodyProps {
  /** 式の中身。 */
  body: Extract<RuleBodyView, { kind: 'formula' }>;
}

/**
 * 式。出す値と、段を上から行の文で並べる（`FormulaLines`）。
 *
 * @param props 式の中身
 * @returns 出す値と式の行の文
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 式の出す値と、段ごとの「名前 ＝ 項 ＋ 項（条件 とき）…」の行の文を描く。
 * @graph-connects FormulaLines [calls] 式の行の文
 */
export function FormulaBody({ body }: FormulaBodyProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <RuleFieldRow label={t('businessLogic.formulaOutput')}>
        <ElementRefText value={body.output} />
      </RuleFieldRow>
      <FormulaLines steps={body.steps} />
    </div>
  );
}

/** `MutationBody` の props。 @graph-connects none */
interface MutationBodyProps {
  /** 変更の中身。 */
  body: Extract<RuleBodyView, { kind: 'mutation' }>;
}

/**
 * 変更。対象 ／ CRUD ／ 変える先。変える先が空（作成・削除）は「—」で出す。
 *
 * @param props 変更の中身
 * @returns 対象・CRUD・変える先
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 変更の対象・CRUD・変える先を欄として描く。
 * @graph-connects none
 */
export function MutationBody({ body }: MutationBodyProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2" data-rule-body="mutation">
      <RuleFieldRow label={t('businessLogic.mutationTarget')}>
        <ElementRefText value={body.target} />
      </RuleFieldRow>
      <RuleFieldRow label={t('businessLogic.mutationCrud')}>
        {t(`businessLogic.crud.${body.crud}`)}
      </RuleFieldRow>
      <RuleFieldRow label={t('businessLogic.mutationTo')}>
        <ElementRefText value={body.to} />
      </RuleFieldRow>
    </div>
  );
}

/** `RuleBodyContent` の props。 @graph-connects none */
interface RuleBodyContentProps extends RuleCompareProps {
  /** 業務ルールの中身。 */
  body: RuleBodyView;
}

/**
 * 業務ルールの中身を形で振り分けて描く。判定表と並べ替えの読み方の断りは表の見出しの ⓘ に畳む。
 * 並べ替えのキーの決め方はここに出さない（業務ルールの画面が畳みで出す）。
 *
 * @param props 業務ルールの中身・描いている面・反対側の姿
 * @returns 形ごとの中身
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの中身を形で振り分け、形ごとの描画へ渡す。
 * @graph-connects DecisionTableBody [calls] 判定表の描画
 * @graph-connects FormulaBody [calls] 式の描画
 * @graph-connects SortBody [calls] 並べ替えの描画
 * @graph-connects MutationBody [calls] 変更の描画
 */
export function RuleBodyContent({ body, side, counterpart }: RuleBodyContentProps) {
  if (body.kind === 'decisionTable') return <DecisionTableBody body={body} note="info" />;
  if (body.kind === 'formula') return <FormulaBody body={body} />;
  if (body.kind === 'sort') {
    return (
      <SortBody
        body={body}
        {...(side === undefined ? {} : { side })}
        {...(counterpart === undefined ? {} : { counterpart })}
      />
    );
  }
  return <MutationBody body={body} />;
}

/** 業務ルール 1 件を描く区画が受け取る props。 @graph-connects none */
interface RuleSectionProps extends RuleCompareProps {
  /** 描く業務ルール。 */
  rule: RuleView;
  /** 引き継ぐ案件番号。選んでいなければ undefined。 */
  pjNumber: number | undefined;
}

/**
 * 入力の「←」の出し手 1 つの描き方。省けば出し手の業務ルールの画面へのリンク。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 入力の値を出す業務ルールを、画面へのリンクかモーダルを開く口として描く描き方
 * @graph-connects none
 */
export type SupplierRender = (supplier: ElementRef) => ReactNode;

/** `RuleIoFields` の props。 @graph-connects none */
interface RuleIoFieldsProps extends RuleSectionProps {
  /** 出力の欄も出すか。業務ルールのモーダル（業務詳細の図）は出力を図の出力の節点で読めるので false。既定は出す。 */
  withOutput?: boolean;
  /** 出し手の描き方。省けば業務ルールの画面へのリンク。 */
  renderSupplier?: SupplierRender;
}

/**
 * 入力と出力。入力の「←」はその値を出す業務ルールで、押すとその業務ルールの画面へ移る（業務ルールの
 * モーダルでは、同じ業務の業務ルールならそのモーダルを開く）。案件の突き合わせでは、反対側に無い入力に
 * 地色を敷く。
 *
 * @param props 業務ルール・案件番号・描いている面・反対側の姿・出力を出すか・出し手の描き方
 * @returns 入力と出力の欄
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールの入力（出す業務ルールへの入口つき）と出力を描き、案件で足した・外した入力に地色を敷く。
 * @graph-connects RuleLink [calls] 出す業務ルールの画面へのリンク
 */
export function RuleIoFields({
  rule,
  pjNumber,
  side,
  counterpart,
  withOutput = true,
  renderSupplier,
}: RuleIoFieldsProps) {
  const { t } = useTranslation();
  const supplierOf =
    renderSupplier ??
    ((supplier: ElementRef) => (
      <RuleLink ruleId={supplier.id} pjNumber={pjNumber}>
        {displayLabel(supplier.label, t, supplier.untranslated)}
      </RuleLink>
    ));
  const otherTermIds =
    counterpart === undefined || counterpart === null
      ? undefined
      : counterpart.inputs.map((input) => input.term.id);

  return (
    <div className="flex flex-col gap-2">
      <RuleFieldRow label={t('businessLogic.inputs')}>
        {rule.inputs.length === 0 ? (
          <span className={MUTED_CLASS}>—</span>
        ) : (
          <span className="flex flex-wrap gap-1.5">
            {rule.inputs.map((input) => {
              const changed = isMissingOn(otherTermIds, input.term.id);
              return (
                <span
                  key={input.term.id}
                  data-rule-input={input.term.id}
                  data-changed={changed}
                  className={`inline-flex flex-wrap items-center gap-1 border border-[var(--color-border)] px-2 py-0.5 text-xs${specDiffCellClass(changed, side)}`}
                >
                  <ElementRefText value={input.term} />
                  {input.suppliers.map((supplier) => (
                    <span key={supplier.id} className={`inline-flex gap-1 ${MUTED_CLASS}`}>
                      ←{supplierOf(supplier)}
                    </span>
                  ))}
                </span>
              );
            })}
          </span>
        )}
      </RuleFieldRow>
      {withOutput && (
        <RuleFieldRow label={t('businessLogic.output')}>
          <span className="font-bold">
            <ElementRefText value={rule.output} />
          </span>
        </RuleFieldRow>
      )}
    </div>
  );
}

/**
 * 読まれる先 — この業務ルールの出力を入力に取る業務ルール。
 *
 * @param props 業務ルール・案件番号
 * @returns 読まれる先のリンクの並び
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business この業務ルールの出力を入力に取る業務ルールを、その画面へのリンクとして並べる。
 * @graph-connects RuleLink [calls] 業務ルールの画面へのリンク
 */
export function RuleReaders({ rule, pjNumber }: RuleSectionProps) {
  const { t } = useTranslation();
  if (rule.readBy.length === 0) {
    return (
      <p className={`text-xs ${MUTED_CLASS}`} data-read-by="0">
        {t('businessLogic.readByEmpty')}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1 text-sm" data-read-by={rule.readBy.length}>
      {rule.readBy.map((reader) => (
        <li key={reader.id}>
          <RuleLink ruleId={reader.id} pjNumber={pjNumber}>
            {displayLabel(reader.label, t, reader.untranslated)}
          </RuleLink>
        </li>
      ))}
    </ul>
  );
}

/**
 * 使っている業務 — 業務詳細へのリンク。
 *
 * **業務詳細のモーダルへは送らない。** 業務ルールは業務から独立しているので、
 * 業務ルールの画面から業務へ移るときに、その業務の中でこの業務ルールを開き直すのは動きとして
 * おかしい（依頼者の指摘）。モーダルで開くのは業務詳細から業務ルールを開くときだけである。
 *
 * @param props 業務ルール・案件番号
 * @returns 使っている業務の並び
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business この業務ルールを集合に載せている業務を、案件を引き継いだ業務詳細へのリンクで並べる。
 * @graph-connects BusinessDetailLink [calls] 業務詳細へのリンク
 */
export function RuleUsedBy({ rule, pjNumber }: RuleSectionProps) {
  const { t } = useTranslation();
  if (rule.usedByBusinesses.length === 0) {
    return (
      <p className={`text-xs ${MUTED_CLASS}`} data-used-by="0">
        {t('businessLogic.usedByEmpty')}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1 text-sm" data-used-by={rule.usedByBusinesses.length}>
      {rule.usedByBusinesses.map((business) => (
        <li key={business.id}>
          <BusinessDetailLink businessId={business.id} pjNumber={pjNumber}>
            {displayLabel(business.label, t, business.untranslated)}
          </BusinessDetailLink>
        </li>
      ))}
    </ul>
  );
}

/** `RuleSections` の props。 @graph-connects none */
interface RuleSectionsProps extends RuleSectionProps {
  /** 入力の出し手の描き方。省けば業務ルールの画面へのリンク。 */
  renderSupplier?: SupplierRender;
}

/**
 * 業務ルールのモーダルの本文 — 入力と中身の 2 つだけ。出力は図の出力の節点で、読まれる先とキーの決め方は
 * 業務ルールの画面で読めるので出さない（Dhik 2026-09-29「情報を絞る」）。節の見出しも置かない。
 *
 * @param props 業務ルール・案件番号・描いている面・反対側の姿・出し手の描き方
 * @returns 入力と中身
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務ルールのモーダルに、業務ルール 1 件の入力（出し手つき）と中身を上から並べて描く。
 * @graph-connects RuleIoFields [calls] 入力
 * @graph-connects RuleBodyContent [calls] 中身 4 種
 */
export function RuleSections({
  rule,
  pjNumber,
  side,
  counterpart,
  renderSupplier,
}: RuleSectionsProps) {
  const compare = {
    ...(side === undefined ? {} : { side }),
    ...(counterpart === undefined ? {} : { counterpart }),
  };
  return (
    <div className="flex flex-col gap-4" data-rule-sections={rule.ruleId}>
      <RuleIoFields
        rule={rule}
        pjNumber={pjNumber}
        withOutput={false}
        {...(renderSupplier === undefined ? {} : { renderSupplier })}
        {...compare}
      />
      <RuleBodyContent body={rule.body} {...compare} />
    </div>
  );
}
